import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const productLinks = [
    { name: 'Gramtek-Public Distribution System', path: '/products/pds' },
    { name: 'Complaint Resolution System', path: '/products/crs' },
    { name: 'Self-Reliance Mission', path: '/products/kachurwahi' },
    { name: 'WhatsApp Integration', path: '/products/whatsapp' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="text-2xl md:text-3xl font-bold text-cyan-500">
              AEPL
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-cyan-500 ${
                scrolled ? 'text-gray-700' : 'text-white/90'
              } ${
                isActive('/') ? 'text-cyan-500' : ''
              }`}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`text-sm font-medium transition-colors hover:text-cyan-500 ${
                scrolled ? 'text-gray-700' : 'text-white/90'
              } ${
                isActive('/about') ? 'text-cyan-500' : ''
              }`}
            >
              About
            </Link>
            
            {/* Products Dropdown */}
            <div 
              className="relative group"
            >
              <button className={`text-sm font-medium transition-colors hover:text-cyan-500 flex items-center gap-1 ${
                scrolled ? 'text-gray-700' : 'text-white/90'
              }`}>
                Solutions
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl py-3 border border-gray-100 z-50">
                {productLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.path}
                    className="block px-6 py-3 text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-600 transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link 
              to="/blog" 
              className={`text-sm font-medium transition-colors hover:text-cyan-500 ${
                scrolled ? 'text-gray-700' : 'text-white/90'
              } ${
                isActive('/blog') ? 'text-cyan-500' : ''
              }`}
            >
              Blog
            </Link>

            <Link 
              to="/contact" 
              className={`text-sm font-medium transition-colors hover:text-cyan-500 ${
                scrolled ? 'text-gray-700' : 'text-white/90'
              } ${
                isActive('/contact') ? 'text-cyan-500' : ''
              }`}
            >
              Contact
            </Link>

            <Link 
              to="/presentation" 
              className={`text-sm font-medium transition-colors hover:text-cyan-500 ${
                scrolled ? 'text-gray-700' : 'text-white/90'
              } ${
                isActive('/presentation') ? 'text-cyan-500' : ''
              }`}
            >
              For Patrons
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button 
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-6 py-2.5 rounded-xl text-sm font-medium shadow-lg shadow-cyan-500/30"
              asChild
            >
              <Link to="/contact">Get Started</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={`lg:hidden p-2 transition-colors ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 bg-white rounded-2xl mt-2 shadow-xl border border-gray-100">
            <nav className="flex flex-col gap-1 px-4">
              <Link 
                to="/" 
                className="text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 px-4 py-3 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                className="text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 px-4 py-3 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <div className="px-4 py-2">
                <div className="text-xs font-semibold text-gray-400 mb-2">SOLUTIONS</div>
                {productLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.path}
                    className="block text-sm text-gray-600 hover:text-cyan-500 py-2 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
              <Link 
                to="/blog" 
                className="text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 px-4 py-3 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
              <Link 
                to="/contact" 
                className="text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 px-4 py-3 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <Link 
                to="/presentation" 
                className="text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 px-4 py-3 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                For Patrons
              </Link>
              <Button 
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white mt-4 mx-4 rounded-xl"
                asChild
              >
                <Link to="/contact" onClick={() => setIsMenuOpen(false)}>Get Started</Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;